import { useRouter } from "next/router";
import React, { useEffect, useRef } from "react";
import { IntlShape } from "react-intl";
import { connect, useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { useForgottenPassword } from "shared/hooks/login";
import { setInitialRequestorConfig } from "shared/lib/requestorFunctions";
// @ts-ignore
import Logger from "shared/services/Logger";

import Img from "@components/FileServer/Img";
import pageWithIntl from "@components/PageWithIntl";
import { generateRandomString } from "@components/Register/Form";
import Alert from "@components/UI/Alert";
import FormattedMessage from "@components/UI/FormattedMessage";
import StandardLayout from "@components/UI/Layouts/StandardLayout";
import Link from "@components/UI/Link";
import PageSeo from "@components/Utils/PageSeo";

import { pageDataLayer } from "@lib/dataLayers";

interface ForgottenPasswordProps {
  intl: IntlShape;
}

function ForgottenPassword({ intl }: ForgottenPasswordProps) {
  const emailRef = useRef(null);
  const router = useRouter();

  const [
    forgottenPasswordStatus,
    submitSendForgottenPassword,
  ] = useForgottenPassword();

  const { isLoading, isFailed, isSuccess } = forgottenPasswordStatus;

  const sendForgottenPasswordForm = (e) => {
    e.preventDefault();
    submitSendForgottenPassword(emailRef.current.value);
  };

  const { user } = useSelector((state: AppState) => {
    return { user: state.user };
  });

  useEffect(() => {
    pageDataLayer("other", user);
  }, [user]);

  return (
    <div className="login-form">
      <PageSeo
        title={{
          defaultTitle: intl.formatMessage({
            id: "Auth.forgottenPassword",
          }),
        }}
      />

      <div className="login-form-inner">
        <div>
          <h1 className="login-title">
            <FormattedMessage id="Auth.forgottenPassword" />
          </h1>
          <p className="login-description">
            <FormattedMessage id="Auth.askPassword" />
          </p>
          {!isSuccess ? (
            <form onSubmit={sendForgottenPasswordForm} method="post">
              <input
                type="hidden"
                name="SecurityContent"
                defaultValue={generateRandomString()}
              />
              <div className="input-wrapper ">
                <span className="input-label" style={{ width: 160 }}>
                  <FormattedMessage id="Form.email" />
                </span>
                <input
                  id="input-login"
                  autoFocus
                  type="text"
                  className="input-input"
                  ref={emailRef}
                  required
                />
              </div>
              {isFailed && (
                <div className="input-wrapper input-wrapper-required">
                  <div className="input-label" />
                  <div className="login-submit-row">
                    <div
                      className="login-error-warning"
                      style={{ maxWidth: 320 }}
                    >
                      <Alert type="warning">
                        <FormattedMessage id="Auth.sendForgottenPasswordError" />
                      </Alert>
                    </div>
                  </div>
                </div>
              )}
              <div className="input-wrapper input-wrapper-required">
                <div className="input-label" />
                <div className="login-submit-row">
                  <button
                    type="submit"
                    id="login-button"
                    className="button-special-offers-offer button-secondary-color modal-submit-button modal-submit-login-button login-submit-button"
                    disabled={isLoading}
                  >
                    {!isLoading ? (
                      <FormattedMessage id="Auth.send" />
                    ) : (
                      <FormattedMessage id="Auth.sending" />
                    )}
                  </button>
                  <Link
                    href={{
                      pathname: "/login",
                      query: {
                        ...(router.query?.isLoginRequired && {
                          isLoginRequired: router.query.isLoginRequired,
                        }),
                        ...(router.query?.redirectAfterLogin && {
                          redirectAfterLogin: router.query.redirectAfterLogin,
                        }),
                      },
                    }}
                  >
                    <span
                      id="login-action-button"
                      className="link pointer login-link-forgotten-password"
                      role="button"
                    >
                      <FormattedMessage id="Screen.Login.title" />
                    </span>
                  </Link>
                </div>
              </div>
            </form>
          ) : (
            <div id="forgotten-password-sent">
              <Alert type="success">
                <FormattedMessage id="Auth.sendForgottenPasswordSuccess" />
              </Alert>
            </div>
          )}
        </div>
        <div className="login-partners">
          <Img src="/static/images/cee-logo.png" alt="CEE Systems" />
          <Img src="/static/images/logo-travelport.png" alt="Travelport" />
          <Img src="/static/images/logo.png" alt="Logo" />
        </div>
      </div>
    </div>
  );
}

function ForgottenPasswordLayout(page) {
  return (
    <StandardLayout showCondensedHeaderLogoStripe hideSearchForm hideFooter>
      {page}
    </StandardLayout>
  );
}

ForgottenPassword.getLayout = ForgottenPasswordLayout;

ForgottenPassword.getInitialProps = async (initialProps) => {
  try {
    const { domain } = await setInitialRequestorConfig(initialProps);
    return { domain };
  } catch (e) {
    Logger.error(`Error Initial index: ${JSON.stringify(e)}`);
    return {};
  }
};

const mapStateToProps = (state: AppState) => ({
  fileServerData: state.requestorConfig.fileServerData,
  textStorage: state.requestorConfig.textStorage,
});

export default connect(mapStateToProps)(pageWithIntl(ForgottenPassword));
